/** Material-section tags on PDP → /tag/:slug listing (must match product.tags[]). */

export function extractProductTagLabel(tag) {
  if (tag == null) return '';
  if (typeof tag === 'string') return tag.trim();
  if (typeof tag === 'object') {
    return String(tag.name ?? tag.tag ?? tag.label ?? '').trim();
  }
  return String(tag).trim();
}

/** URL slug for /tag/:tag (lowercase, spaces → hyphens). */
export function tagToUrlSlug(label) {
  return normalizeTagKey(label);
}

export function normalizeTagKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export function tagLabelsFromProduct(product) {
  const tags = product?.tags;
  if (!Array.isArray(tags)) return [];
  return tags.map(extractProductTagLabel).filter(Boolean);
}

/** Decode route param and optional navigation state from PDP click. */
export function resolveTagRouteContext(tagParam, locationState) {
  const rawParam = String(tagParam || '').trim();
  let decodedParam = rawParam;
  try {
    decodedParam = decodeURIComponent(rawParam);
  } catch {
    decodedParam = rawParam;
  }
  const labelFromState =
    locationState && typeof locationState.tagLabel === 'string'
      ? locationState.tagLabel.trim()
      : '';
  const displayName =
    labelFromState ||
    decodedParam.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return { rawParam, decodedParam, labelFromState, displayName };
}

export function buildTagMatchKeys(tagParam, labelFromState) {
  const { decodedParam } = resolveTagRouteContext(tagParam, { tagLabel: labelFromState });
  const keys = new Set();
  const add = (v) => {
    const k = normalizeTagKey(v);
    if (k) keys.add(k);
  };
  if (labelFromState) add(labelFromState);
  add(decodedParam);
  add(tagParam);
  add(decodedParam.replace(/-/g, ' '));
  add(String(tagParam).replace(/-/g, ' '));
  return keys;
}

export function productMatchesTag(product, tagParam, labelFromState = '') {
  const wanted = buildTagMatchKeys(tagParam, labelFromState);
  if (!wanted.size) return false;
  return tagLabelsFromProduct(product).some((label) => wanted.has(normalizeTagKey(label)));
}

export function filterProductsByTag(products, tagParam, labelFromState = '') {
  const list = Array.isArray(products) ? products : [];
  return list.filter((p) => productMatchesTag(p, tagParam, labelFromState));
}

/** Query values to try on GET /products?tag= (backend may expect label vs slug). */
export function tagQueryCandidates(tagParam, labelFromState = '') {
  const { decodedParam, labelFromState: label } = resolveTagRouteContext(tagParam, {
    tagLabel: labelFromState,
  });
  const out = [];
  const push = (v) => {
    const s = String(v || '').trim();
    if (s && !out.includes(s)) out.push(s);
  };
  if (label) push(label);
  push(decodedParam);
  push(decodedParam.replace(/-/g, ' '));
  push(tagParam);
  return out;
}

export function formatTagPageTitle(displayName) {
  const s = String(displayName || '').trim();
  if (!s) return 'Tag';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
